/**
 * @fileoverview Data-bound field rendering with host-injected resolution and write-back.
 * @module inspection-renderer/fields
 *
 * Responsibilities:
 * - Resolve data-bound widget values and metadata through the host context.
 * - Render editable controls or a formatted read-only surface.
 * - Commit host writes only on control commit events.
 *
 * Dependencies:
 * - `@firesquirrel/core-domain` formatting and widget metadata.
 * - Renderer context and shared field wrapper.
 *
 * Architecture:
 * - Host-agnostic renderer; persistence is delegated through ResolveContext.
 *
 * Notes:
 * - Text-like inputs commit on blur to avoid per-keystroke persistence.
 * - Unresolved synthetic bound tokens render as placeholders, not raw values.
 *
 * @component DataBoundField
 * Purpose: Renders a value bound to a host data source.
 * Props: field, value, resolved?, bulkPilotMode?, onChange?, readOnly?, wasModifiedByInspector?, requiredUnmet?, requiredImmediate?, errorMessage?.
 * Behavior: Resolves metadata and lookup options, updates form state on input, and writes on commit.
 * Data Source: hybrid (host-provided snapshots and local form state).
 * Constraints: Bulk-pilot and forced read-only modes never render editable controls.
 * Performance: Reads synchronous host context; no local database or network access.
 */
import { LockKeyhole } from 'lucide-react';
import { useState } from 'react';
import { formatDataBoundValue, mergeDataBoundWidgetMeta } from '@firesquirrel/core-domain';
import type { DataBoundWidgetMeta, InspectionFormField, ResolvedFieldStateV2 } from '@firesquirrel/core-domain';
import { useResolveContext } from '../contracts/rendererContext';
import type { LookupOption } from '../contracts/resolveContext';
import { isUnansweredDataBoundValue, isUnresolvedDbSentinel } from '../validation/dataBoundFieldValue';
import { FieldWrapper } from './FieldWrapper';

export interface DataBoundFieldProps {
  field: InspectionFormField;
  value: unknown;
  resolved?: ResolvedFieldStateV2;
  bulkPilotMode?: boolean;
  onChange?: (next: string | number | boolean | null) => void;
  readOnly?: boolean;
  wasModifiedByInspector?: boolean;
  requiredUnmet?: boolean;
  requiredImmediate?: boolean;
  errorMessage?: string;
}

export function DataBoundField({
  field,
  value,
  resolved,
  bulkPilotMode,
  onChange,
  readOnly = false,
  wasModifiedByInspector,
  requiredUnmet,
  requiredImmediate,
  errorMessage,
}: DataBoundFieldProps) {
  const extras = (field.extras ?? {}) as Record<string, unknown>;
  const ctx = useResolveContext();
  const registeredMeta = typeof extras.widgetId === 'string'
    ? ctx?.resolveWidgetDef?.(extras.widgetId)
    : undefined;
  const meta = mergeDataBoundWidgetMeta(registeredMeta, {
    widgetId: typeof extras.widgetId === 'string' ? extras.widgetId : undefined,
    dataSource: typeof extras.dataSource === 'string' ? extras.dataSource : undefined,
    boundField: typeof extras.boundField === 'string' ? extras.boundField : undefined,
    compositeFields: Array.isArray(extras.compositeFields)
      ? extras.compositeFields.filter((part): part is string => typeof part === 'string')
      : undefined,
    displayFormat: typeof extras.displayFormat === 'string' ? extras.displayFormat : undefined,
    inputType: typeof extras.inputType === 'string' ? extras.inputType : undefined,
    defaultMode: typeof extras.defaultMode === 'string' ? extras.defaultMode : undefined,
    lookupType: typeof extras.lookupType === 'string' ? extras.lookupType : undefined,
  });
  const { widgetId, dataSource, boundField, compositeFields, displayFormat, inputType, defaultMode, lookupType } = meta;
  const mode = extras.mode as string | undefined;
  const responseValue = isUnresolvedDbSentinel(field, value) ? undefined : value;
  const forceReadOnly = extras.forceReadOnly === true
    || (registeredMeta as (DataBoundWidgetMeta & { forceReadOnly?: boolean }) | undefined)?.forceReadOnly === true;
  const editable = !readOnly && !bulkPilotMode && !forceReadOnly
    && (mode === 'editable' || (!mode && defaultMode === 'editable'))
    && (typeof onChange === 'function' || typeof ctx?.writeBoundField === 'function');

  if (extras.requiredModule && ctx && !ctx.hasModule(extras.requiredModule as 'contacts' | 'violations' | 'header')) {
    return null;
  }

  const resolvedFromContext = isUnansweredDataBoundValue(responseValue)
    && ctx
    && dataSource
    && (boundField || compositeFields?.length || registeredMeta)
    ? ctx.resolveBoundField({
        dataSource,
        boundField: boundField ?? '',
        widgetId,
        displayFormat,
        composite: compositeFields,
        serviceAreaTypeCode: extras.serviceAreaTypeCode as string | undefined,
        entityCategoryFilter: extras.entityCategoryFilter as string | undefined,
      })
    : null;
  const normalizedResolvedValue = isUnresolvedDbSentinel(field, resolvedFromContext) ? null : resolvedFromContext;
  const raw = isUnansweredDataBoundValue(responseValue) ? normalizedResolvedValue : responseValue;
  const [draftValue, setDraftValue] = useState<unknown>(undefined);
  const hasDraftValue = draftValue !== undefined;
  const controlRaw = hasDraftValue ? draftValue : raw;
  const isServiceArea = widgetId === 'entity.serviceArea' || boundField === 'serviceAreas';
  const serviceAreaTypeCode = extras.serviceAreaTypeCode as string | undefined;
  const display = raw == null
    ? String(isServiceArea && !serviceAreaTypeCode
      ? '(select service area type)'
      : extras.resolvedLabel ?? extras.placeholder ?? '—')
    : formatDataBoundValue(raw, displayFormat);
  const optionList = Array.isArray(extras.options) ? extras.options as LookupOption[] : [];
  const lookupOptions = lookupType ? ctx?.resolveLookup(lookupType) ?? [] : [];
  const options = lookupOptions.length > 0 ? lookupOptions : optionList;
  const showInvalid = !!errorMessage || (!!requiredUnmet && !!requiredImmediate);
  const classes = `w-full px-3 py-2 rounded-xl text-sm border ${showInvalid ? 'border-red-500 bg-red-50/50 text-red-900 ring-2 ring-red-500/20' : editable ? 'border-slate-300 bg-white text-slate-900 shadow-2xs focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20' : 'border-slate-200 bg-slate-100/90 text-slate-500 cursor-not-allowed select-none'}`;
  const dataAttributes = {
    'data-resolved': raw == null ? 'false' : 'true',
    'aria-invalid': showInvalid,
    'data-data-source': dataSource ?? '',
    'data-bound-field': boundField ?? '',
    'data-default-mode': defaultMode ?? 'readOnly',
    'data-force-read-only': forceReadOnly ? 'true' : 'false',
    'data-bulk-pilot-locked': bulkPilotMode ? 'true' : 'false',
    'data-was-modified': wasModifiedByInspector ? 'true' : 'false',
    'data-input-type': inputType ?? '',
    'data-display-format': displayFormat ?? '',
  };
  const change = (next: string | number | boolean | null) => onChange?.(next);
  const commit = (next: unknown) => {
    if (dataSource && boundField) {
      ctx?.writeBoundField?.({ dataSource, boundField, value: next, widgetId, fieldId: field.id });
    }
  };
  const textValue = controlRaw == null ? '' : typeof controlRaw === 'object'
    ? String((controlRaw as Record<string, unknown>).code ?? (controlRaw as Record<string, unknown>).name ?? '')
    : String(controlRaw);

  const control = editable ? (
    inputType === 'textarea' ? (
      <textarea id={field.id} className={classes} value={textValue} {...dataAttributes}
        onChange={(event) => { setDraftValue(event.target.value); change(event.target.value); }}
        onBlur={(event) => { commit(hasDraftValue ? draftValue : event.currentTarget.value); setDraftValue(undefined); }} />
    ) : inputType === 'toggle' ? (
      <input id={field.id} type="checkbox" checked={Boolean(controlRaw)} {...dataAttributes}
        onChange={(event) => { change(event.target.checked); commit(event.target.checked); }} />
    ) : inputType === 'dropdown' || inputType === 'lookup' ? (
      <select id={field.id} className={classes} value={textValue} {...dataAttributes}
        onChange={(event) => {
          const next = event.target.value;
          change(next);
          const selected = options.find((option) => option.value === next);
          const rawIsLookupReference = raw != null
            && typeof raw === 'object'
            && !Array.isArray(raw)
            && 'code' in raw;
          const writesLookupReference = rawIsLookupReference
            || Boolean(lookupType)
            || displayFormat === 'lookup'
            || inputType === 'lookup';
          const rawReference = rawIsLookupReference ? raw as Record<string, unknown> : undefined;
          const rawLookupId = rawReference && typeof rawReference.lookupId === 'string'
            ? rawReference.lookupId
            : undefined;
          const rawLookupType = rawReference && typeof rawReference.lookupType === 'string'
            ? rawReference.lookupType
            : undefined;
          const selectedLookupType = selected?.lookupType ?? lookupType ?? rawLookupType;
          const catalogOption = selected && !selected.lookupId && selectedLookupType
            ? ctx?.resolveLookup(selectedLookupType).find((option) => option.value === selected.value)
            : undefined;
          const reuseCurrentLookupId = rawReference?.code === selected?.value
            ? rawLookupId
            : undefined;
          const lookupId = selected?.lookupId ?? catalogOption?.lookupId ?? reuseCurrentLookupId;
          commit(selected && writesLookupReference
            ? {
                ...(lookupId ? { lookupId } : {}),
                ...(selectedLookupType ? { lookupType: selectedLookupType } : {}),
                code: selected.value,
                name: selected.label,
              }
            : next);
        }}>
        <option value="">{String(extras.placeholder ?? 'Select…')}</option>
        {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    ) : (
        <input id={field.id} type={inputType === 'number' ? 'number' : inputType === 'date' ? 'date' : 'text'}
        className={classes}
        value={inputType === 'date' ? textValue.slice(0, 10) : textValue}
        {...dataAttributes}
        onChange={(event) => {
          const next = inputType === 'number'
            ? (event.target.value === '' ? null : Number(event.target.value))
            : event.target.value;
          if (inputType !== 'date') setDraftValue(next);
          change(next);
          if (inputType === 'date') { commit(next); setDraftValue(undefined); }
        }}
        onBlur={(event) => {
          if (inputType !== 'date') {
            const next = inputType === 'number'
              ? (hasDraftValue ? draftValue : event.currentTarget.value === '' ? null : Number(event.currentTarget.value))
              : (hasDraftValue ? draftValue : event.currentTarget.value);
            commit(next);
            setDraftValue(undefined);
          }
        }} />
    )
  ) : (
    <div role="textbox" aria-readonly="true" aria-label={field.label} className={`${classes} flex items-center gap-1`}
      {...dataAttributes}>
      <LockKeyhole size={14} aria-hidden="true" />
      <span>{display}</span>
    </div>
  );

  return (
    <FieldWrapper fieldId={field.id} widgetType="dataBound" label={field.label} helpText={field.helpText}
      required={field.required} resolved={resolved} isNA={false} naEnabled={false} requiredUnmet={requiredUnmet} requiredImmediate={requiredImmediate} errorMessage={errorMessage}>
      {control}
    </FieldWrapper>
  );
}
